import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export interface Profile {
  id: string;
  user_id: string;
  email: string;
  status: 'pending_payment' | 'active' | 'disabled';
  created_at: string;
  updated_at: string;
}

export function useProfile() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }

      return data as Profile | null;
    } catch (error) {
      console.error('Unexpected error fetching profile:', error);
      return null;
    }
  };

  const ensureProfileExists = async (userId: string, email: string) => {
    try {
      // First check if profile exists
      let profile = await fetchProfile(userId);
      
      if (!profile) {
        // Create profile if it doesn't exist
        const { data, error } = await supabase
          .from('profiles')
          .insert({
            user_id: userId,
            email: email,
            status: 'pending_payment'
          })
          .select()
          .single();

        if (error) {
          console.error('Error creating profile:', error);
          return null;
        }

        profile = data as Profile;
      }

      return profile;
    } catch (error) {
      console.error('Error ensuring profile exists:', error);
      return null;
    }
  };

  const refreshProfile = async () => {
    if (!user) return;
    
    const profileData = await fetchProfile(user.id);
    setProfile(profileData);
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event);
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Ensure profile exists and fetch it
          setTimeout(() => {
            ensureProfileExists(session.user.id, session.user.email || '').then(setProfile);
          }, 0);
        } else {
          setProfile(null);
        }

        setLoading(false);
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        ensureProfileExists(session.user.id, session.user.email || '').then(setProfile);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: undefined // Disable email confirmation for MVP
      }
    });
    
    return { data, error };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      setUser(null);
      setSession(null);
      setProfile(null);
    }
    return { error };
  };

  const isAuthenticated = !!session?.user;
  const isActive = profile?.status === 'active';
  const isPendingPayment = profile?.status === 'pending_payment';

  return {
    user,
    session,
    profile,
    loading,
    isAuthenticated,
    isActive,
    isPendingPayment,
    signUp,
    signIn,
    signOut,
    refreshProfile,
  };
}